import { useState } from "react";

import {
  Bookmark,
  ClipboardList,
  Download,
  Eye,
  FileText,
  Mail,
  Presentation,
  Settings2,
  Swords,
  type LucideIcon,
} from "lucide-react";

import type { AssetCard as AssetCardType, DocumentType } from "@/api/client";
import {
  ASSET_STATUS_LABEL,
  AUDIENCE_BAND,
  AUDIENCE_LABEL,
  DOCUMENT_TYPE_SINGULAR,
  FORMAT_TINT,
} from "@/lib/asset-labels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  asset: AssetCardType;
  onPreview: (asset: AssetCardType) => void;
  onDownload: (asset: AssetCardType) => void;
  onEmail: (asset: AssetCardType) => void;
  onToggleStar: (asset: AssetCardType) => void;
  /** Admin-only (RM/RA): opens the manage slide-out. Absent for consumers. */
  onManage?: (asset: AssetCardType) => void;
  busy?: boolean;
}

/** Icon stand-in for the redesign's left-hand thumbnail strip — the real hero
 * image is deferred to CHP-348. */
const DOC_TYPE_ICON: Record<DocumentType, LucideIcon> = {
  one_pager: FileText,
  battlecard: Swords,
  slide_deck: Presentation,
  case_study: ClipboardList,
};

/** One resource card in the grid (CHP-272/273 redesign): a narrow vertical
 * thumbnail strip on the left; file-format metadata + a top-right bookmark
 * toggle in the header; a minimal icon-driven footer (Preview / Download / Email).
 * Category tags (document type + topic/industry) use a filled style distinct
 * from the outlined file-format tags. Admins also get a Manage action. */
export function AssetCard({
  asset,
  onPreview,
  onDownload,
  onEmail,
  onToggleStar,
  onManage,
  busy,
}: Props) {
  const versionLabel = asset.version_number != null ? `v${asset.version_number}` : null;
  const ThumbIcon = DOC_TYPE_ICON[asset.document_type];
  // Prefer the real hero image (CHP-348); fall back to the document-type icon
  // when there's none, or if the presigned URL fails to load (e.g. expired).
  const [heroFailed, setHeroFailed] = useState(false);
  const showHero = asset.hero_image_url != null && !heroFailed;
  const categoryTags = [...asset.topics, ...asset.industries];
  // CHP-959: colour-code the card by audience (enablement vs client-facing).
  const band = AUDIENCE_BAND[asset.audience];

  return (
    <Card className="flex flex-row overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lifted">
      {/* CHP-959: the icon rail doubles as the audience colour band — a thin
          solid left accent (`band.band`) over an audience-tinted strip — so
          enablement vs client-facing reads at a glance. The icon is top-aligned
          (pt-5 matches CardHeader's padding) so it sits level with the
          file-format/version/status pill row (CHP-432). */}
      <div
        className={cn(
          "flex w-12 flex-shrink-0 items-start justify-center overflow-hidden border-l-4",
          band.strip,
          band.band,
          showHero ? "" : "pt-5",
        )}
      >
        {showHero ? (
          <img
            src={asset.hero_image_url ?? undefined}
            alt=""
            data-testid="asset-hero-image"
            className="h-full w-full object-cover"
            onError={() => setHeroFailed(true)}
          />
        ) : (
          <ThumbIcon className="h-6 w-6" aria-hidden />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <CardHeader className="gap-2">
          {/* File-format metadata header (left) + bookmark toggle (top-right). */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {asset.format ? (
                // CHP-959 (secondary): a very slight per-format colour cue.
                <Badge variant="outline" className={cn(FORMAT_TINT[asset.format])}>
                  {asset.format}
                </Badge>
              ) : null}
              {versionLabel ? <Badge variant="outline">{versionLabel}</Badge> : null}
              {asset.status !== "published" ? (
                <Badge variant="outline">{ASSET_STATUS_LABEL[asset.status]}</Badge>
              ) : null}
            </div>
            {/* Toggle button: the accessible name stays constant and aria-pressed
                carries the on/off state (W3C APG button pattern). */}
            <Button
              variant="ghost"
              size="icon-sm"
              className="-mr-1 -mt-1"
              aria-pressed={asset.starred}
              aria-label={`Bookmark ${asset.title}`}
              disabled={busy}
              onClick={() => onToggleStar(asset)}
            >
              <Bookmark
                className={cn("h-4 w-4", asset.starred && "fill-current text-warning")}
                aria-hidden
              />
            </Button>
          </div>
          {/* CHP-959: audience eyebrow tinted to match the colour band. */}
          <p className={cn("text-xs font-medium", band.label)}>{AUDIENCE_LABEL[asset.audience]}</p>
          <h3 className="text-base font-semibold leading-snug text-foreground">{asset.title}</h3>
        </CardHeader>

        <CardContent className="flex-1 space-y-3">
          {asset.description ? (
            <p className="line-clamp-3 text-sm text-muted-foreground">{asset.description}</p>
          ) : null}
          {/* Category tags — neutral grey fill, distinct from the outlined
              file-format tags in the header (the fill-vs-border contrast carries
              the distinction; no colour, so nothing reads as good/bad). The
              document type leads in a slightly stronger ink. */}
          <div className="flex flex-wrap gap-1.5">
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
              {DOCUMENT_TYPE_SINGULAR[asset.document_type]}
            </span>
            {categoryTags.map((term) => (
              <span
                key={term.id}
                className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {term.label}
              </span>
            ))}
          </div>
        </CardContent>

        <CardFooter className="gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Preview ${asset.title}`}
            onClick={() => onPreview(asset)}
          >
            <Eye className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Download ${asset.title}`}
            onClick={() => onDownload(asset)}
          >
            <Download className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Email ${asset.title} to client`}
            onClick={() => onEmail(asset)}
          >
            <Mail className="h-4 w-4" aria-hidden />
          </Button>
          {onManage ? (
            <Button
              variant="ghost"
              size="icon-sm"
              className="ml-auto"
              aria-label={`Manage ${asset.title}`}
              onClick={() => onManage(asset)}
            >
              <Settings2 className="h-4 w-4" aria-hidden />
            </Button>
          ) : null}
        </CardFooter>
      </div>
    </Card>
  );
}
